import React from 'react';
import { useBranding } from '../hooks/useBranding';
import { PLATFORM_TENANT, safeHttpUrl } from '../services/brandingService';
import { BootPulse } from './BootPulse';
import { QMark } from './QMark';
import './BrandedLayout.css';

interface BrandedLayoutProps {
  children: React.ReactNode;
  tenantId?: string;
}

/**
 * Layout that wears the current page's branding: during an application's
 * sign-in, the branding of the tenant that owns the application (its name,
 * logo, colour, privacy policy and terms); on QAIM's own pages, QAIM's.
 * A branding failure never blocks the page: it renders with QAIM's defaults.
 */
export const BrandedLayout: React.FC<BrandedLayoutProps> = ({ children, tenantId }) => {
  const { branding, loading } = useBranding(tenantId);

  if (loading) {
    return (
      <div className="branded-layout loading">
        <BootPulse label="Loading" />
      </div>
    );
  }

  const platform = branding.tenant_id === PLATFORM_TENANT;
  const name = platform ? 'QAIM' : branding.name || 'QAIM';
  const logo = safeHttpUrl(branding.logo_url);
  const legalLinks = [
    { label: 'Privacy Policy', href: safeHttpUrl(branding.privacy_policy_url) },
    { label: 'Terms of Use', href: safeHttpUrl(branding.terms_of_use_url) },
  ].filter((link): link is { label: string; href: string } => link.href !== null);

  return (
    <div className="branded-layout">
      <header className="branded-header">
        {/* A plain link, not a router Link: home re-checks the session. */}
        <a href="/" className="branded-home" aria-label={`${name} home`}>
          {logo ? (
            <img src={logo} alt="" className="branded-logo" />
          ) : platform ? (
            <QMark size={32} className="branded-mark" />
          ) : null}
          <span className={platform ? 'branded-wordmark' : 'branded-name'}>{name}</span>
        </a>
      </header>
      <main className="branded-content">{children}</main>
      <footer className="branded-footer">
        <p>
          &copy; {new Date().getFullYear()} {platform ? 'QAIM' : name}. All rights reserved.
        </p>
        {legalLinks.length > 0 && (
          <nav className="branded-legal" aria-label="Legal">
            {legalLinks.map((link) => (
              <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer">
                {link.label}
              </a>
            ))}
          </nav>
        )}
        {!platform && <p className="branded-powered">Sign-in by QAIM</p>}
      </footer>
    </div>
  );
};
